import {createSlice, createSelector} from '@reduxjs/toolkit';
import {hasRole, ROLES} from '../../utils/permissions';

/**
 * Users slice now stores a flat list of users (each with a roles[] array).
 * `technicians` remains a separate bucket because it represents a different
 * entity (technicians table), not users with the technician role.
 */
const initialState = {
  list: [],
  technicians: [],
  loading: true,
  error: '',
};

// Backwards-compat shim: callers that still pass a single `role` are routed
// into a one-element roles[]. Allows incremental rollout of multi-role.
const normalizeUser = (u) => {
  if (!u) return u;
  if (Array.isArray(u.roles)) return u;
  if (u.role) return {...u, roles: [u.role]};
  return {...u, roles: []};
};

// Heuristic: payloads tagged with role==='technician' or coming from the
// technicians entity dialogs go to `technicians`; everything else (real users
// with a roles[] from the API) goes to `list`.
const isTechnicianEntityPayload = (payload) =>
  !!payload && (payload.role === 'technician' || (!Array.isArray(payload.roles) && !payload.role));

const usersSlice = createSlice({
  name: 'users',
  initialState,
  reducers: {
    setUsers(state, action) {
      const {list, technicians, error} = action.payload || {};
      if (Array.isArray(list)) state.list = list.map(normalizeUser);
      if (Array.isArray(technicians)) state.technicians = technicians;
      state.loading = false;
      state.error = error || '';
    },
    addUser(state, action) {
      const payload = action.payload;
      if (isTechnicianEntityPayload(payload)) {
        state.technicians.push(payload);
        return;
      }
      state.list.push(normalizeUser(payload));
    },
    updateUser(state, action) {
      const updated = action.payload;
      if (!updated) return;

      // If an existing technician (entity) row is being updated, merge in place
      // so computed fields (e.g. total_earnings) survive partial updates.
      const techIdx = state.technicians.findIndex((t) => t.id === updated.id);
      if (techIdx !== -1) {
        state.technicians[techIdx] = {...state.technicians[techIdx], ...updated};
        return;
      }

      // If marked explicitly as technician entity, push there.
      if (isTechnicianEntityPayload(updated)) {
        state.technicians.push(updated);
        return;
      }

      const normalized = normalizeUser(updated);
      const idx = state.list.findIndex((u) => u.id === normalized.id);
      if (idx === -1) state.list.push(normalized);
      else state.list[idx] = {...state.list[idx], ...normalized};
    },
    removeUsers() {
      return initialState;
    },
  },
});

// ---------------------------------------------------------------------------
// Selectors
// ---------------------------------------------------------------------------

/** Full slice (loading / error / list / technicians). */
export const getUsers = (state) => state.users;
export const getUsersList = (state) => state.users.list;
export const getTechnicians = (state) => state.users.technicians;

const selectList = (state) => state.users.list;

export const makeSelectUsersByRole = (role) =>
  createSelector([selectList], (list) => list.filter((u) => hasRole(u, role)));

export const getAdmins = createSelector([selectList], (list) =>
  list.filter((u) => hasRole(u, ROLES.ADMIN))
);

export const getDispatchers = createSelector([selectList], (list) =>
  list.filter((u) => hasRole(u, ROLES.DISPATCHER))
);

const isUserActive = (u) => u && u.is_active !== false;

export const getActiveAdmins = createSelector([selectList], (list) =>
  list.filter((u) => hasRole(u, ROLES.ADMIN) && isUserActive(u))
);

export const getActiveDispatchers = createSelector([selectList], (list) =>
  list.filter((u) => hasRole(u, ROLES.DISPATCHER) && isUserActive(u))
);

export const getActiveUsersList = createSelector([selectList], (list) =>
  list.filter(isUserActive)
);

export const getAccountants = createSelector([selectList], (list) =>
  list.filter((u) => hasRole(u, ROLES.ACCOUNTANT))
);

export const getGeneralManagers = createSelector([selectList], (list) =>
  list.filter((u) => hasRole(u, ROLES.GENERAL_MANAGER))
);

// Actions
export const {setUsers, addUser, updateUser, removeUsers} = usersSlice.actions;

export default usersSlice.reducer;
